import type { DragEndEvent } from "@dnd-kit/core";
import type { QueryClient } from "@tanstack/react-query";
import { foldersService, type LibraryFolder } from "@/services/foldersService";

type ToastFn = (opts: { title: string; description?: string; variant?: "destructive" }) => void;
import type { MastermindSpace } from "@shared/schema";

export type CombinedFolderOrSpace =
  | { type: "folder"; data: LibraryFolder }
  | { type: "space"; data: MastermindSpace };

export type LibraryFolderTreeDragState = {
  folders: LibraryFolder[];
  searchQuery: string;
  filteredFolders: LibraryFolder[];
  combinedSortedList: CombinedFolderOrSpace[];
};

function isDescendant(
  folders: LibraryFolder[],
  folderId: string,
  potentialParentId: string
): boolean {
  const folder = folders.find((f) => f.id === folderId);
  if (!folder) return false;
  if (folder.parentId === potentialParentId) return true;
  if (folder.parentId) {
    return isDescendant(folders, folder.parentId, potentialParentId);
  }
  return false;
}

/**
 * Folder reorder and folder-into-folder moves from the library sidebar sortable tree.
 * Document drags are handled elsewhere.
 */
export async function runLibraryFolderTreeDragEnd(
  event: DragEndEvent,
  tree: LibraryFolderTreeDragState,
  queryClient: QueryClient,
  toast: ToastFn
): Promise<void> {
  const { active, over } = event;
  if (!over || active.id === over.id) return;

  const activeIdStr = String(active.id);
  const overIdStr = String(over.id);

  if (activeIdStr.startsWith("doc-")) return;

  const draggedFolderId = activeIdStr;

  if (overIdStr.startsWith("droppable-space-") || overIdStr.startsWith("droppable-topic-")) {
    return;
  }

  if (overIdStr.startsWith("droppable-")) {
    const targetFolderId = overIdStr.replace("droppable-", "");

    if (targetFolderId === draggedFolderId) return;
    if (isDescendant(tree.folders, targetFolderId, draggedFolderId)) {
      toast({
        title: "Cannot move folder",
        description: "Cannot move a folder into its own descendant",
        variant: "destructive",
      });
      return;
    }

    try {
      await foldersService.updateFolder(draggedFolderId, { parentId: targetFolderId });
      await queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });
      toast({ title: "Folder moved", description: "Folder moved to new parent" });
    } catch {
      toast({
        title: "Error moving folder",
        variant: "destructive",
      });
    }
    return;
  }

  const folderIds = tree.searchQuery
    ? tree.filteredFolders.map((f) => f.id)
    : tree.combinedSortedList.filter((i) => i.type === "folder").map((i) => i.data.id);
  const oldIndex = folderIds.indexOf(active.id as string);
  const newIndex = folderIds.indexOf(over.id as string);

  if (oldIndex === -1 || newIndex === -1) return;

  try {
    await foldersService.updateFolder(draggedFolderId, { sortOrder: newIndex });
    await queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });
    toast({ title: "Folder order updated" });
  } catch {
    toast({
      title: "Error updating folder order",
      variant: "destructive",
    });
  }
}
